import {Fragment, ReactElement, useCallback, useEffect, useState} from "react";
import {v4 as uuidv4} from "uuid";
import classnames from "classnames/bind";

import {SocialShuffleChat, PostService, ShuffledComment, REFRESH_SHUFFLE_AI, useMyUser} from "../../data";
import {Span4, P2} from "../../components/text";
import {Button, Checkbox, InputRow, MultiColumn, Validate, required} from "../../components/input";
import {Badge} from "../../components/badge";
import {Icon} from "../../components/images";
import {Loading} from "../../components/loading";
import {MentionsText} from "../../components/input/mentions-text";
import {useMutationToast} from "../../toast";
import {useDebounceCallback} from "../../hooks/use-debounce-callback";
import {Setter} from "../../types";

import styles from "./shuffle-caption.module.scss";

const bStyles = classnames.bind(styles);

interface ShuffleCaptionProps {
	prompt: string;
	disabled: boolean;
	comments: ShuffleCommentChange[];
	setComments: Setter<ShuffleCommentChange[]>;
	setValid: Setter<boolean>;
}

export interface ShuffleCommentChange {
	shuffle: ShuffledComment;
	service: PostService;
	aiChat?: SocialShuffleChat;
}

interface AISuggestionProps {
	chat: SocialShuffleChat;
	onAdd: (chat: SocialShuffleChat) => void;
	onUpdate: (chat: SocialShuffleChat) => void;
}

const AISuggestion = ({chat, onAdd, onUpdate}: AISuggestionProps): ReactElement => {
	const [refreshSocialShuffle, {loading}] = useMutationToast(REFRESH_SHUFFLE_AI, {
		onCompleted: ({refreshSocialShuffle}) => onUpdate(refreshSocialShuffle),
	});
	const handleRefresh = useCallback(
		() =>
			refreshSocialShuffle({
				variables: {
					aiChat: {
						id: chat.id,
						message: "",
						service: chat.service,
						prompt: chat.prompt,
					},
				},
			}),
		[chat, refreshSocialShuffle]
	);

	const debouncedRefresh = useDebounceCallback(handleRefresh, 400);

	useEffect(() => {
		if (!chat.message) debouncedRefresh();
	}, [chat, debouncedRefresh]);

	return (
		<div className={styles.aiSuggestion}>
			{loading ? (
				<Loading position="center" />
			) : (
				<>
					<InputRow>
						<Span4>{chat.message}</Span4>
						<Icon icon="refresh" onClick={handleRefresh} color="blue" />
					</InputRow>
					<Button invert border={false} onClick={() => onAdd(chat)} value="Add to my captions" />
				</>
			)}
		</div>
	);
};

export const ShuffleCaption = ({
	prompt,
	disabled,
	comments,
	setComments,
	setValid,
}: ShuffleCaptionProps): ReactElement => {
	const me = useMyUser();
	const isAIEnabled = me.org.options.ai;

	const [optimizeForTwitter, setOptimizeForTwitter] = useState(true);

	const [titleAiChat, setTitleAiChat] = useState<SocialShuffleChat>({
		id: uuidv4(),
		prompt,
		message: "",
		service: "GENERAL",
	});

	useEffect(() => setTitleAiChat(c => ({...c, message: "", prompt})), [prompt]);

	const handleTwitterOptimization = useCallback(
		(optimizeForTwitter: boolean) => {
			setOptimizeForTwitter(optimizeForTwitter);
			setComments(c =>
				c.map(comment => ({
					...comment,
					aiChat: comment.aiChat
						? {
								...comment.aiChat,
								service: optimizeForTwitter ? "TWITTER" : "GENERAL",
								id: uuidv4(),
								message: "",
						  }
						: undefined,
				}))
			);
			setOptimizeForTwitter(optimizeForTwitter);
		},
		[setOptimizeForTwitter, setComments]
	);

	const handleAdd = useCallback(
		(chat: SocialShuffleChat) =>
			setComments(a => {
				const newChange: ShuffleCommentChange = {
					shuffle: {openGraph: {comment: chat.message}, isAIGenerated: true},
					service: "GENERAL",
					aiChat: {
						id: uuidv4(),
						message: "",
						prompt: chat.message,
						service: optimizeForTwitter ? "TWITTER" : "GENERAL",
					},
				};

				if (chat.id === titleAiChat?.id) {
					setTitleAiChat(c => ({...c, message: ""}));
				}

				return [...a, newChange].map(c => (c.aiChat?.id === chat.id ? {...c, aiChat: undefined} : c));
			}),
		[setComments, optimizeForTwitter, titleAiChat]
	);

	const handleAIUpdate = useCallback(
		(aiChat: SocialShuffleChat) =>
			setComments(a => a.map(c => (c.aiChat?.id === aiChat.id ? {...c, aiChat} : c))),
		[setComments]
	);

	const handleCommentChange = useCallback(
		(index: number, comment: string) => {
			let aiChat = comments[index].aiChat;

			if (comment.length > 3) {
				aiChat = {
					id: uuidv4(),
					message: "",
					service: optimizeForTwitter ? "TWITTER" : comments[index].service,
					prompt: comment.trim(),
				};
			}

			setComments(comments => {
				const newComments = [...comments];
				newComments[index] = {
					...newComments[index],
					shuffle: {
						...newComments[index].shuffle,
						openGraph: {comment},
						isAIGenerated: newComments[index].shuffle.isAIGenerated && comment.length > 3,
					},
					aiChat,
				};

				return newComments;
			});
		},
		[comments, setComments, optimizeForTwitter]
	);

	const handleCommentDelete = useCallback(
		(index: number) =>
			setComments(c => {
				const newComments = [...c];
				newComments.splice(index, 1);
				return newComments;
			}),
		[setComments]
	);

	const handleCommentAdd = useCallback(
		() =>
			setComments(c => [
				...c,
				{shuffle: {openGraph: {comment: ""}, isAIGenerated: false}, service: "GENERAL"},
			]),
		[setComments]
	);

	return (
		<div className={bStyles("container", {disabled, aiDisabled: !isAIEnabled})}>
			<div className={bStyles("captionsContainer", {disabled, aiDisabled: !isAIEnabled})}>
				<P2 bold color="grey">
					Create alternative captions that will be randomized for the recipients of this post.
				</P2>

				<Validate setStatus={setValid}>
					<MultiColumn columns={3} className={bStyles("captionInputRow", {disabled})}>
						{comments.map(({service, shuffle}, index) => (
							<Fragment key={index}>
								<MentionsText
									type="textarea"
									onChange={v => handleCommentChange(index, v)}
									value={shuffle.openGraph.comment || ""}
									info={`Characters: ${shuffle.openGraph.comment?.length || 0}`}
									validate={required}
									disabled={disabled}
									service={service}
								/>
								{!disabled && (
									<Button
										border={false}
										invert
										icon="delete"
										color="black"
										onClick={() => handleCommentDelete(index)}
									/>
								)}
							</Fragment>
						))}
					</MultiColumn>
				</Validate>

				{!disabled && (
					<Button
						disabled={comments.length >= 20}
						onClick={handleCommentAdd}
						value="Add another"
						icon="add"
						border={false}
						invert
					/>
				)}
			</div>
			{!disabled && isAIEnabled && (
				<div className={styles.suggestionsContainer}>
					<InputRow position="left">
						<div className={styles.aiGenerated}>
							<Span4>Suggested captions</Span4>
							<Badge text="AI Generated" color="pink" size="small" icon="robot" />
						</div>
					</InputRow>
					<Checkbox
						label="Optimize for X posts (255 character limit)"
						value={optimizeForTwitter}
						onChange={handleTwitterOptimization}
					/>
					<div className={styles.aiSuggestions}>
						{titleAiChat.prompt && (
							<AISuggestion onUpdate={setTitleAiChat} onAdd={handleAdd} chat={titleAiChat} />
						)}

						{comments
							.filter(c => c.aiChat)
							.map(({aiChat}, i) => (
								<AISuggestion
									onUpdate={handleAIUpdate}
									onAdd={handleAdd}
									chat={aiChat as SocialShuffleChat}
									key={i}
								/>
							))}
					</div>
				</div>
			)}
		</div>
	);
};
